import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TTFTByModel } from '@/api/dashboard';

interface TTFTByModelChartProps {
  data?: TTFTByModel[];
  isLoading: boolean;
  error?: Error | null;
}

/**
 * Chart displaying Time To First Token (TTFT) metrics by day
 * Shows average TTFT as bars, with min/max as lines
 */
const TTFTByModelChart: React.FC<TTFTByModelChartProps> = ({
  data,
  isLoading,
  error,
}) => {
  const cardStyle = {
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    height: 300,
    minHeight: 300,
  };

  // Transform data for display
  const chartData = data?.map((item) => ({
    date: item.date,
    'Average (ms)': Math.round(item.avg_ttft),
    'Min (ms)': Math.round(item.min_ttft),
    'Max (ms)': Math.round(item.max_ttft),
    count: item.count,
  })) || [];

  return (
    <Card sx={cardStyle}>
      <CardContent sx={{ height: '80%' }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 1, fontSize: '1rem' }}>
          Time To First Token (TTFT) Over Time
        </Typography>

        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error.message}</Alert>
        ) : !chartData || chartData.length === 0 ? (
          <Typography
            sx={{
              color: '#94a3b8',
              textAlign: 'center',
              py: 4,
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            No TTFT data available
          </Typography>
        ) : (
          <ResponsiveContainer width="100%">
            <ComposedChart data={chartData} margin={{ top: 30, right: 40, left: 10, bottom: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis
                dataKey="date"
                stroke="#94a3b8"
                fontSize="0.7rem"
                tick={{ fill: '#94a3b8' }}
              />
              <YAxis
                stroke="#94a3b8"
                tick={{ fill: '#94a3b8' }}
                fontSize="0.7rem"
                label={{ value: 'TTFT (ms)',fontSize:"0.8rem", angle: -90, position: 'insideLeft' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '8px',
                  color: '#fff',
                }}
                formatter={(value: any) => [
                  `${Number(value).toLocaleString()} ms`,
                ]}
              />
              <Legend wrapperStyle={{fontSize: "0.8rem"}}/>
              <Bar
                dataKey="Average (ms)"
                fill="#3b82f6"
                radius={[8, 8, 0, 0]}
              />
              <Line
                type="monotone"
                dataKey="Min (ms)"
                stroke="#22c55e"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="Max (ms)"
                stroke="#ef4444"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default TTFTByModelChart;
